import React, { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { useI18n } from '../i18n/I18nContext';

const DISMISS_KEY = 'kmt_install_dismissed';
const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// "Add to Home Screen" banner: native prompt on Android/desktop, step-by-step hint on iOS Safari.
export default function InstallPrompt() {
  const { t } = useI18n();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === '1') return;
    if (ios) { setShow(true); return; }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    const onInstalled = () => setShow(false);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => { window.removeEventListener('beforeinstallprompt', onPrompt); window.removeEventListener('appinstalled', onInstalled); };
  }, [ios]);

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, '1'); setShow(false); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => null);
    setDeferred(null); setShow(false);
  };

  if (!show) return null;
  return (
    <div className="fixed left-3 right-3 bottom-20 md:bottom-5 md:left-auto md:right-5 md:w-96 z-50 kmt-panel p-4 flex gap-3 items-start"
      style={{ border: '1px solid var(--kmt-gold)', boxShadow: '0 12px 40px rgba(0,0,0,0.55)' }} data-testid="install-prompt">
      <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-lg shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-sm">{t('install.title')}</div>
        {ios ? (
          <div className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--kmt-muted)' }} data-testid="install-ios-steps">
            {t('install.ios1')} <Share size={12} className="inline -mt-0.5" style={{ color: 'var(--kmt-gold)' }} /> {t('install.ios2')}
          </div>
        ) : (
          <>
            <div className="text-xs mt-1" style={{ color: 'var(--kmt-muted)' }}>{t('install.desc')}</div>
            <button className="kmt-btn-gold mt-3 flex items-center gap-2 text-xs" onClick={install} data-testid="install-app-btn">
              <Download size={14} />{t('install.btn')}
            </button>
          </>
        )}
      </div>
      <button onClick={dismiss} aria-label="dismiss" data-testid="install-dismiss-btn" style={{ color: 'var(--kmt-muted)' }}><X size={16} /></button>
    </div>
  );
}
